/**
 * Én rad i avgjørelseskøen. ÉN delt komponent, med vilje.
 *
 * Relevansscore vises KUN på crawler-rader. En merkedag du selv har planlagt har
 * ingen relevansscore å vurderes mot, og et tall der leser som en kvalitetsdom
 * over eget innhold. Feilen dukket opp i fem av seks genererte mockups, også de
 * som eksplisitt ba om det motsatte — derfor bor regelen ett sted, ikke tre.
 * Se docs/plan-gjenstaende-gap.md Spor F.
 */
import React from 'react';
import { Clock, Loader2, Link2, Calendar, Users, Timer, Trash2 } from 'lucide-react';
import type { Article } from '../../../types';
import { Button } from '../../ui/Button';
import { formatReadingTime } from '../../../utils/articleText';

interface Props {
  article: Article;
  busy?: boolean;
  onPublish: (a: Article) => void;
  onLater: (a: Article) => void;
  onOpen: (a: Article) => void;
  onDelete: (a: Article) => void;
}

/** Firestore Timestamp | Date | ms → «12. aug». Tom streng når datoen mangler. */
function shortDate(value: unknown): string {
  const d =
    value && typeof (value as { toDate?: () => Date }).toDate === 'function'
      ? (value as { toDate: () => Date }).toDate()
      : value instanceof Date
        ? value
        : typeof value === 'number'
          ? new Date(value)
          : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
}

/** Trinnene i _generateArticleDraftCore, i rekkefølge. */
const STEP_LABEL: Record<string, string> = {
  youtube: 'Henter relevante videoer',
  context: 'Leser tidligere artikler',
  writing: 'Skriver artikkelen',
  image: 'Lager illustrasjon',
};

function firstImage(a: Article): string | null {
  if (!Array.isArray(a.images) || a.images.length === 0) return null;
  return [...a.images].sort((x, y) => (x.order || 0) - (y.order || 0))[0]?.url || null;
}

const ArticleInboxRow: React.FC<Props> = ({ article, busy, onPublish, onLater, onOpen, onDelete }) => {
  // To-trinns sletting i lista: første trykk arm, andre sletter. Ingen modal for
  // en handling som skjer ofte, men heller ikke ett uhell unna.
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const img = firstImage(article);
  const isGenerating = article.status === 'generating';

  // Kilden avgjør hva som er meningsfull metadata på raden.
  const fromWheel = !!article.wheel_entry_id;
  const sourceName = (article as { source_site_name?: string }).source_site_name;
  const relevance = (article as { relevance_score?: number }).relevance_score;
  const showRelevance = !fromWheel && typeof relevance === 'number';

  const dateLabel = shortDate(
    article.status === 'published' ? article.published_at : article.created_at,
  );
  const readTime = formatReadingTime(article.content || '');

  if (isGenerating) {
    return (
      <div className="flex items-center gap-3 sm:gap-5 px-3 sm:px-5 py-4 border-b border-[var(--border-primary)] last:border-b-0">
        <div className="w-24 sm:w-44 aspect-video flex-shrink-0 rounded-card bg-[var(--bg-surface-alt)] animate-pulse" />
        <div className="flex-1 min-w-0 space-y-2.5">
          <div className="h-4 w-2/3 rounded-card bg-[var(--bg-surface-alt)] animate-pulse" />
          <div className="h-3 w-1/3 rounded-card bg-[var(--bg-surface-alt)] animate-pulse" />
        </div>
        {/* Ekte trinn fra genereringen, ikke en anonym spinner. Generering tar
            1–2 minutter — da er det verdt å si hvor den er. */}
        <span className="flex items-center gap-1.5 text-xs text-emerald-400 font-bold flex-shrink-0">
          <Loader2 size={12} className="animate-spin" />
          {STEP_LABEL[String((article as { _generation_step?: string })._generation_step || '')] || 'Genererer'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 px-3 sm:px-5 py-4 border-b border-[var(--border-primary)] last:border-b-0 hover:bg-[var(--bg-surface-alt)]/40 transition">
      <button
        type="button"
        onClick={() => onOpen(article)}
        className="flex items-start sm:items-center gap-3 sm:gap-5 flex-1 min-w-0 text-left rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
      >
        {img ? (
          <img src={img} alt="" className="w-24 sm:w-44 aspect-video flex-shrink-0 rounded-card object-cover" />
        ) : (
          <div className="w-24 sm:w-44 aspect-video flex-shrink-0 rounded-card bg-[var(--bg-surface-alt)]" />
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm sm:text-lg font-bold text-[var(--text-primary)] leading-snug line-clamp-2">
            {article.title || 'Uten tittel'}
          </p>
          {article.summary && (
            <p className="text-xs text-[var(--text-muted)] leading-relaxed line-clamp-1 mt-1">
              {article.summary}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap mt-1">
            {/* Utkast ligger i samme kø, men skal være tydelig merket — de er
                ubesluttet arbeid, ikke ferske forslag. */}
            {article.status === 'draft' && (
              <span className="text-tiny font-black uppercase tracking-widest px-2 py-0.5 rounded-pill bg-[var(--bg-surface-alt)] text-[var(--text-muted)]">
                Utkast
              </span>
            )}

            {fromWheel ? (
              <span className="text-tiny font-black uppercase tracking-widest px-2 py-0.5 rounded-pill bg-emerald-500/15 text-emerald-400">
                Årshjul
              </span>
            ) : sourceName ? (
              <span className="flex items-center gap-1 text-tiny font-black uppercase tracking-widest px-2 py-0.5 rounded-pill bg-[var(--bg-surface-alt)] text-[var(--text-muted)]">
                <Link2 size={9} /> {sourceName}
              </span>
            ) : null}

            {/* Kun crawler-rader. Se filkommentaren. */}
            {showRelevance && (
              <span
                className="text-tiny font-black tabular-nums px-2 py-0.5 rounded-pill bg-[var(--bg-surface-alt)] text-emerald-400"
                title="Relevansscore fra triage"
              >
                {relevance}
              </span>
            )}

            {/* Metadatatettheten er det som skiller en ferdig rad fra en tom.
                Alt under finnes eller er avledbart — se
                docs/plan-artikkelmodul-innholdsgap.md 3a. */}
            {dateLabel && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-muted)] tabular-nums">
                <Calendar size={10} /> {dateLabel}
              </span>
            )}

            {article.target_audience && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
                <Users size={10} /> {article.target_audience}
              </span>
            )}

            {readTime && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-muted)] tabular-nums">
                <Timer size={10} /> {readTime}
              </span>
            )}

            {article.occasion_ymd && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-muted)] tabular-nums">
                <Clock size={10} /> {article.occasion_ymd}
              </span>
            )}

            {article.distribute_via_email && (
              <span className="text-tiny font-black uppercase tracking-widest px-2 py-0.5 rounded-pill bg-amber-500/15 text-amber-400">
                E-post
              </span>
            )}
          </div>
        </div>
      </button>

      <div className="flex sm:flex-col gap-2 flex-shrink-0 w-full sm:w-36">
        <Button size="md" variant="primary" fullWidth className="flex-1 sm:flex-none" disabled={busy} onClick={() => onPublish(article)}>
          Publiser
        </Button>
        <div className="flex gap-2 flex-1 sm:flex-none">
          <Button size="md" variant="secondary" fullWidth disabled={busy} onClick={() => onLater(article)}>
            Senere
          </Button>
          {/* Slett må finnes i lista. Uten den måtte du åpne editoren for å bli
              kvitt et utkast — som er hvorfor åtte gamle utkast ble liggende. */}
          <Button
            size="md"
            variant={confirmDelete ? 'danger' : 'ghost'}
            disabled={busy}
            aria-label={confirmDelete ? 'Bekreft sletting' : 'Slett'}
            title={confirmDelete ? 'Trykk igjen for å slette' : 'Slett'}
            onClick={() => (confirmDelete ? onDelete(article) : setConfirmDelete(true))}
            onBlur={() => setConfirmDelete(false)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ArticleInboxRow;
